export const CONTENT_DIFFICULTY_OPTIONS = ["EASY", "MEDIUM", "HARD"];
export const CONTENT_TYPE_OPTIONS = ["MANUAL"];
export const CONTENT_LEVEL_OPTIONS = ["A", "B", "C", "D", "E"];

export const CONTENT_DEFAULTS = {
    author: "ReadWell",
    level: "A",
    imageUrl: "no-image.jpg",
    categories: [],
    difficulty: "MEDIUM",
    contentType: "MANUAL",
};

const toArray = (value) => (Array.isArray(value) ? value : []);

export const sanitizeCategories = (value) => {
    if (Array.isArray(value)) {
        return value
            .map((item) => String(item || "").trim())
            .filter(Boolean);
    }

    return String(value || "")
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
};

export const cleanParagraphs = (paragraphs = []) => {
    return toArray(paragraphs)
        .map((item) => String(item?.paragraph || "").trim())
        .filter(Boolean)
        .map((paragraph, index) => ({ id: index + 1, paragraph }));
};

export const normalizeContentItem = (item = {}) => {
    const paragraphs = cleanParagraphs(item.paragraphs || []);
    const categories = sanitizeCategories(item.categories || []);

    return {
        id: item.id || item._id || item.contentId || "",
        title: String(item.title || "").trim(),
        author: String(item.author || CONTENT_DEFAULTS.author).trim(),
        level: String(item.level || CONTENT_DEFAULTS.level).trim() || CONTENT_DEFAULTS.level,
        imageUrl: item.image || item.imageUrl || CONTENT_DEFAULTS.imageUrl,
        paragraphs,
        categories,
        difficulty: String(item.difficulty || CONTENT_DEFAULTS.difficulty).toUpperCase(),
        contentType: String(item.contentType || CONTENT_DEFAULTS.contentType).toUpperCase(),
        createdAt: item.createdAt || "",
        paragraphCount: paragraphs.length,
    };
};

export const normalizeContentListResponse = (response = {}) => {
    const books = response?.data?.books;
    return toArray(books).map(normalizeContentItem);
};

export const normalizeSingleContentResponse = (response = {}) => {
    return normalizeContentItem(response?.data?.book || response?.data || {});
};

export const emptyContentDraft = {
    title: "",
    author: CONTENT_DEFAULTS.author,
    level: CONTENT_DEFAULTS.level,
    imageUrl: CONTENT_DEFAULTS.imageUrl,
    categoriesInput: "",
    difficulty: CONTENT_DEFAULTS.difficulty,
    contentType: CONTENT_DEFAULTS.contentType,
    paragraphs: [{ id: 1, paragraph: "" }],
};

export const contentToDraft = (content = {}) => {
    const paragraphs = toArray(content.paragraphs).length
        ? content.paragraphs.map((item, index) => ({
            id: index + 1,
            paragraph: item.paragraph || "",
        }))
        : [{ id: 1, paragraph: "" }];

    return {
        title: content.title || "",
        author: content.author || CONTENT_DEFAULTS.author,
        level: content.level || CONTENT_DEFAULTS.level,
        imageUrl: content.imageUrl || CONTENT_DEFAULTS.imageUrl,
        categoriesInput: toArray(content.categories).join(", "),
        difficulty: content.difficulty || CONTENT_DEFAULTS.difficulty,
        contentType: content.contentType || CONTENT_DEFAULTS.contentType,
        paragraphs,
    };
};

export const validateContentDraft = (draft = {}) => {
    const title = String(draft.title || "").trim();
    const paragraphs = cleanParagraphs(draft.paragraphs || []);
    const errors = {};

    if (!title) {
        errors.title = "Title is required.";
    }

    if (!paragraphs.length) {
        errors.paragraphs = "At least one paragraph is required.";
    }

    return {
        isValid: Object.keys(errors).length === 0,
        errors,
    };
};

export const buildCreateContentPayload = (draft = {}) => {
    const cleanedParagraphs = cleanParagraphs(draft.paragraphs || []);

    return {
        title: String(draft.title || "").trim(),
        author: String(draft.author || CONTENT_DEFAULTS.author).trim() || CONTENT_DEFAULTS.author,
        level: String(draft.level || CONTENT_DEFAULTS.level).trim() || CONTENT_DEFAULTS.level,
        imageUrl: String(draft.imageUrl || CONTENT_DEFAULTS.imageUrl).trim() || CONTENT_DEFAULTS.imageUrl,
        paragraphs: cleanedParagraphs,
        categories: sanitizeCategories(draft.categoriesInput || draft.categories || []),
        difficulty:
            String(draft.difficulty || CONTENT_DEFAULTS.difficulty).toUpperCase() ||
            CONTENT_DEFAULTS.difficulty,
        contentType:
            String(draft.contentType || CONTENT_DEFAULTS.contentType).toUpperCase() ||
            CONTENT_DEFAULTS.contentType,
    };
};

const valuesEqual = (first, second) => JSON.stringify(first) === JSON.stringify(second);

export const buildUpdateContentPayload = (previousContent = {}, draft = {}) => {
    const previous = normalizeContentItem(previousContent);
    const next = buildCreateContentPayload(draft);
    const payload = {};

    ["title", "author", "level", "imageUrl", "difficulty", "contentType"].forEach((key) => {
        if (!valuesEqual(previous[key], next[key])) {
            payload[key] = next[key];
        }
    });

    if (!valuesEqual(previous.categories, next.categories)) {
        payload.categories = next.categories;
    }

    if (!valuesEqual(previous.paragraphs, next.paragraphs)) {
        payload.paragraphs = next.paragraphs;
    }

    return payload;
};

export const formatContentDate = (value) => {
    if (!value) return "Unknown";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "Unknown";

    return new Intl.DateTimeFormat("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
    }).format(date);
};

export const getContentErrorMessage = (error = {}) => {
    const status = error?.status || error?.response?.status || 0;
    const backendMessage = error?.message || error?.response?.data?.error || "";

    if (status === 403) {
        return "You do not have permission to manage this content.";
    }

    if (status === 404) {
        return "Reading content not found.";
    }

    return backendMessage || "Something went wrong. Please try again.";
};
